import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { ImageModalComponent } from '../image-modal/image-modal';
import { CameraCapacitorService } from 'src/app/services/camera-capacitor.service';
import { DocumentPickerCapacitorService } from 'src/app/services/document-picker-capacitor.service';
import { DocumentScanService } from 'src/app/services/document-scan.service';
import { EventsService } from 'src/app/services/events.service';
import { FileService } from 'src/app/services/file.service';
import { LanguageService } from 'src/app/services/language.service';
import { PlatformService } from 'src/app/services/platform.service';
import {
  ScanComponent,
  ScanField,
  ScanFieldOptionColumn,
  ScanResponse,
  ScanResponseStatus
} from '../../models/document-scan.model';
import { Subject, timer } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';

type DocumentScanViewState = 'idle' | 'uploading' | 'processing' | 'pending_user' | 'pending_review' | 'error';

@Component({
  selector: 'document-scan',
  templateUrl: 'document-scan.component.html',
  styleUrls: ['document-scan.component.scss']
})
export class DocumentScanComponent implements OnInit, OnDestroy {
  @Input() componentId?: number;
  @Input() forceItemListMode = false;
  @Input() isFullScreen = false;
  @ViewChild('cameraInput') cameraInput: ElementRef<HTMLInputElement>;
  @ViewChild('galleryInput') galleryInput: ElementRef<HTMLInputElement>;
  @ViewChild('documentInput') documentInput: ElementRef<HTMLInputElement>;
  @ViewChild('successSection') successMessageEl: ElementRef<HTMLElement>;

  public initConfig: any;
  public scanComponent: ScanComponent;
  public state: DocumentScanViewState = 'idle';
  public scanForm: UntypedFormGroup = new UntypedFormGroup({});
  public orderedFields: ScanField[] = [];
  public isFormInvalid = false;
  public errorMessage = '';
  public successMessage = '';
  public responseId: number | null = null;
  public responseData: ScanResponse;
  public previewUrl = '';
  public isSlowProcessing = false;
  public processingSeconds = 0;
  public isLoadingComponent = false;
  public forbidden = false;
  public canView = true;
  public canEdit = true;
  public isRecoverableError = true;

  private initialEditableData: Record<string, any> = {};
  private processingStartTime = 0;
  private destroy$ = new Subject<void>();
  private stopPolling$ = new Subject<void>();

  constructor(
    private documentScanService: DocumentScanService,
    private cameraCapacitorService: CameraCapacitorService,
    private documentPickerCapacitor: DocumentPickerCapacitorService,
    private fileService: FileService,
    private platformService: PlatformService,
    private router: Router,
    private events: EventsService,
    private actionSheetController: ActionSheetController,
    private languageService: LanguageService,
    private modalController: ModalController,
    private cdr: ChangeDetectorRef
  ) {
    this.initConfig = this.documentScanService.documentScanInitConfig;
  }

  ngOnInit(): void {
    const id = this.componentId || this.initConfig?.id;
    if (!id) {
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-NOT-AVAILABLE';
      this.isRecoverableError = false;
      return;
    }
    this.loadComponent(id);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isButtonView(): boolean {
    if (this.forceItemListMode) return false;
    return this.initConfig?.view?.mode === 'button';
  }

  public isListView(): boolean {
    if (this.forceItemListMode) return true;
    return this.initConfig?.view?.mode === 'item-list';
  }

  public getShowHeader(): boolean {
    return !!this.initConfig?.view?.showHeader && !this.isFullScreen;
  }

  public getButtonText(): string {
    return this.scanComponent?.buttonText || this.initConfig?.buttonText || 'ADD-IMAGE';
  }

  public goToFullDocumentScan(): void {
    const id = this.scanComponent?.id || this.initConfig?.id;
    if (!id) return;
    this.router.navigate([`/document-scan/${id}/list`]);
  }

  public async openImageModal(): Promise<void> {
    if (!this.previewUrl) return;
    const imageModal = await this.modalController.create({
      component: ImageModalComponent,
      componentProps: { images: [{ url: this.previewUrl }] }
    });
    await imageModal.present();
  }

  public async showImageSourceActionSheet(): Promise<void> {
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheets',
      buttons: [
        {
          text: await this.languageService.translate('DOCUMENT-SCAN-TAKE-PHOTO'),
          handler: async () => {
            await this.openCamera();
          }
        },
        {
          text: await this.languageService.translate('DOCUMENT-SCAN-CHOOSE-GALLERY'),
          handler: async () => {
            await this.openGallery();
          }
        },
        {
          text: await this.languageService.translate('OPTION_SELECT_DOCUMENT'),
          handler: async () => {
            await this.openDocument();
          }
        },
        {
          text: await this.languageService.translate('CANCEL'),
          role: 'cancel'
        }
      ]
    });

    await actionSheet.present();
  }

  public async openCamera(): Promise<void> {
    if (this.platformService.isWeb()) {
      this.cameraInput?.nativeElement?.click();
      return;
    }
    const photo = await this.cameraCapacitorService.takePhoto();
    if (!photo) return;
    const result = await this.photoToFile(photo, photo.format || 'jpeg');
    if (result) this.uploadPhotoResult(result);
  }

  public async openGallery(): Promise<void> {
    if (this.platformService.isWeb()) {
      this.galleryInput?.nativeElement?.click();
      return;
    }
    const photos = await this.cameraCapacitorService.selectPicture();
    const selected = photos?.photos?.[0];
    if (!selected) return;
    const result = await this.photoToFile(selected, selected.format || 'jpeg');
    if (result) this.uploadPhotoResult(result);
  }

  /**
   * Sube el resultado de cámara/galería. En native usa uploadFromBase64 para evitar
   * problemas con Blob/File en iOS. En web o cuando hace falta comprimir, usa uploadSelectedFile.
   */
  private uploadPhotoResult(result: {
    file: File;
    knownSize: number;
    knownMimeType: string;
    previewDataUrl: string;
    base64Data?: string;
  }): void {
    const meta = {
      knownSize: result.knownSize,
      knownMimeType: result.knownMimeType,
      previewDataUrl: result.previewDataUrl,
      base64Data: result.base64Data
    };
    const needsCompress = result.knownMimeType.startsWith('image/') && result.knownSize > DocumentScanComponent.MAX_FILE_BYTES;
    if (!this.platformService.isWeb() && result.base64Data && !needsCompress) {
      this.uploadFromBase64(result.base64Data, result.knownMimeType, result.file.name, result.knownSize);
    } else {
      this.uploadSelectedFile(result.file, meta);
    }
  }

  public async openDocument(): Promise<void> {
    if (this.platformService.isWeb()) {
      this.documentInput?.nativeElement?.click();
      return;
    }
    try {
      const result = await this.documentPickerCapacitor.pickDocument();
      if (!result?.success || !result?.document) {
        this.state = 'error';
        this.errorMessage = 'DOCUMENT-SCAN-FILE-FORMAT-INVALID';
        this.isRecoverableError = true;
        return;
      }
      const doc = result.document;
      const base64 = doc.originalData ?? doc.base64Data;
      if (base64 && doc.type) {
        this.uploadFromBase64(base64, doc.type, doc.name, doc.size);
        return;
      }
      const file = this.documentResultToFile(result);
      if (file) {
        this.uploadSelectedFile(file);
      } else {
        this.state = 'error';
        this.errorMessage = 'DOCUMENT-SCAN-FILE-FORMAT-INVALID';
        this.isRecoverableError = true;
      }
    } catch (error) {
      console.error('[document-scan] Error picking document:', error);
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-GENERIC-ERROR';
      this.isRecoverableError = true;
    }
  }

  private uploadFromBase64(base64Data: string, mimeType: string, fileName?: string, knownSize?: number): void {
    if (!this.scanComponent?.id) return;
    if (this.state === 'uploading' || this.state === 'processing') return;
    const allowedMime = ['image/jpeg', 'image/png', 'image/webp'];
    if (!mimeType || !allowedMime.includes(mimeType)) {
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-FILE-FORMAT-INVALID';
      this.isRecoverableError = true;
      return;
    }
    const size = knownSize ?? Math.ceil((base64Data.length * 3) / 4);
    if (size === 0 || size > DocumentScanComponent.MAX_FILE_BYTES) {
      this.state = 'error';
      this.errorMessage = size === 0 ? 'DOCUMENT-SCAN-FILE-EMPTY' : 'DOCUMENT-SCAN-FILE-SIZE-INVALID';
      this.isRecoverableError = true;
      return;
    }
    const ext = mimeType.includes('png') ? 'png' : mimeType.includes('webp') ? 'webp' : 'jpg';
    const name = (fileName && /\.(jpg|jpeg|png|webp)$/i.test(fileName)) ? fileName : `document.${ext}`;
    this.errorMessage = '';
    this.successMessage = '';
    this.state = 'uploading';
    this.documentScanService.uploadImageFromBase64(this.scanComponent.id, base64Data, mimeType, name).subscribe({
      next: (response) => {
        this.responseId = response.id;
        this.state = 'processing';
        this.startPolling(response.id);
      },
      error: (error: HttpErrorResponse) => {
        this.state = 'error';
        const mappedError = this.mapHttpError(error);
        this.errorMessage = mappedError.message;
        this.isRecoverableError = mappedError.recoverable;
      }
    });
  }

  /**
   * Normaliza cualquier objeto file-like a un File válido.
   * Siempre crea un File nuevo para evitar estructuras corruptas de Safari/iOS.
   * Prioriza value.blob sobre value (instanceof) para objetos con estructura {blob, name, type}.
   */
  private normalizeToFile(value: any): File | null {
    if (!value) return null;
    if (typeof value !== 'object') return null;
    let blobSource: Blob | ArrayBuffer | null = null;
    let fileName = `document-${Date.now()}.jpg`;
    let mimeType = 'image/jpeg';
    const getType = (v: any): string => (typeof v?.type === 'string' ? v.type : v?.type?.type) || '';

    if (value.blob instanceof Blob) {
      blobSource = value.blob;
      const nameVal = value.document_name ?? value.name;
      fileName = typeof nameVal === 'string' ? nameVal : fileName;
      mimeType = getType(value) || value.blob.type || 'image/jpeg';
    } else if (Array.isArray(value.name) && value.name.length > 0) {
      const first = value.name[0];
      if (first instanceof Blob || first instanceof File) {
        blobSource = first;
        mimeType = getType(value) || (first.type || '') || 'image/jpeg';
        const firstName = first instanceof File ? first.name : (first as any).name;
        fileName = typeof firstName === 'string' && firstName ? firstName : `document-${Date.now()}.jpg`;
      }
    } else if (value instanceof Blob || value instanceof File) {
      blobSource = value;
      mimeType = getType(value) || (value.type || '') || 'image/jpeg';
      const nameVal = value instanceof File ? value.name : (value as any).name;
      fileName = typeof nameVal === 'string' && nameVal ? nameVal : `document-${Date.now()}.jpg`;
    } else if (value.blob) {
      blobSource = new Blob([value.blob], { type: getType(value) || 'application/octet-stream' });
      const nameVal = value.document_name ?? value.name;
      fileName = typeof nameVal === 'string' ? nameVal : fileName;
      mimeType = getType(value) || 'image/jpeg';
    }
    if (blobSource) return new File([blobSource], fileName, { type: mimeType });
    return null;
  }

  private documentResultToFile(result: any): File | null {
    if (!result?.success || !result?.document) return null;
    const doc = result.document;
    if (doc instanceof File) return doc;
    if (doc.blob instanceof Blob) {
      const blob = doc.blob;
      const name = doc.document_name || doc.name || 'document';
      const type = doc.type || blob.type || 'application/octet-stream';
      return blob instanceof File ? blob : new File([blob], name, { type });
    }
    return null;
  }

  public onFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target?.files?.[0];
    if (file) this.uploadSelectedFile(file);
    if (target) target.value = '';
  }

  public getFieldControl(field: ScanField): AbstractControl {
    return this.scanForm.get(String(field.fieldId));
  }

  public isFieldError(field: ScanField): boolean {
    const control = this.getFieldControl(field);
    return !!control && control.invalid && (control.touched || this.isFormInvalid);
  }

  public getColumns(field: ScanField): ScanFieldOptionColumn[] {
    return field.metadata?.columns || [];
  }

  public getColumnKey(column: ScanFieldOptionColumn): string {
    return column.id || column.field || column.name || '';
  }

  public getColumnLabel(column: ScanFieldOptionColumn): string {
    return column.label || column.name || column.id || '';
  }

  public getRows(field: ScanField): any[] {
    const control = this.getFieldControl(field);
    return Array.isArray(control?.value) ? control.value : [];
  }

  public trackByRowIndex = (index: number): number => {
    return index;
  };

  public trackByColumnName = (_index: number, column: ScanFieldOptionColumn): string => {
    return column.id || column.field || column.name || '';
  };

  public addTableRow(field: ScanField): void {
    const control = this.getFieldControl(field);
    const currentRows = this.getRows(field);
    const row: any = {};
    this.getColumns(field).forEach((column) => {
      const key = this.getColumnKey(column);
      row[key] = column.type === 'number' ? null : '';
    });
    control.setValue([...currentRows, row]);
    control.markAsDirty();
    control.markAsTouched();
  }

  public removeTableRow(field: ScanField, index: number): void {
    const control = this.getFieldControl(field);
    const currentRows = [...this.getRows(field)];
    currentRows.splice(index, 1);
    control.setValue(currentRows);
    control.markAsDirty();
    control.markAsTouched();
  }

  public updateTableCellInput(field: ScanField, rowIndex: number, column: ScanFieldOptionColumn, event: any): void {
    const value = event.target?.value ?? event.detail?.value ?? '';
    const control = this.getFieldControl(field);
    const currentRows = this.getRows(field);
    if (!currentRows[rowIndex]) return;
    const key = this.getColumnKey(column);
    const normalizedValue = this.normalizeTableCellValue(column.type, value);
    
    // Actualizar directamente el objeto existente sin crear nuevas referencias del objeto row
    currentRows[rowIndex][key] = normalizedValue;
    
    // Usar requestAnimationFrame para diferir la actualización del FormControl
    // Esto evita el parpadeo y la pérdida de foco
    requestAnimationFrame(() => {
      control.setValue([...currentRows], { emitEvent: true });
      control.markAsDirty();
      control.markAsTouched();
    });
  }

  public updateTableCell(field: ScanField, rowIndex: number, column: ScanFieldOptionColumn, value: any): void {
    const control = this.getFieldControl(field);
    const currentRows = this.getRows(field);
    if (!currentRows[rowIndex]) return;
    const key = this.getColumnKey(column);
    const normalizedValue = this.normalizeTableCellValue(column.type, value);
    
    // Actualizar directamente el objeto existente
    currentRows[rowIndex][key] = normalizedValue;
    // Actualizar el FormControl
    control.setValue([...currentRows], { emitEvent: true });
    control.markAsDirty();
    control.markAsTouched();
  }

  public onSubmit(): void {
    if (!this.responseId) return;
    if (this.scanForm.invalid) {
      this.isFormInvalid = true;
      this.markFormTouched();
      return;
    }
    this.isFormInvalid = false;
    const payload = this.buildSubmitPayload();
    this.documentScanService.submitEdits(this.responseId, payload).subscribe({
      next: (response) => {
        this.successMessage = response.message || 'DOCUMENT-SCAN-SUBMIT-SUCCESS';
        this.state = response.status === 'pending_review' ? 'pending_review' : this.state;
        if (response.status === 'pending_review') {
          this.scrollToSuccessMessage();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.state = 'error';
        const mappedError = this.mapHttpError(error);
        this.errorMessage = mappedError.message;
        this.isRecoverableError = mappedError.recoverable;
      }
    });
  }

  private scrollToSuccessMessage(): void {
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.successMessageEl?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  public onCancelEdit(): void {
    this.scanForm.patchValue(this.initialEditableData);
    this.scanForm.markAsPristine();
    this.isFormInvalid = false;
  }

  public scanAnotherDocument(): void {
    this.stopPolling();
    this.responseId = null;
    this.responseData = null;
    this.previewUrl = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.isSlowProcessing = false;
    this.processingSeconds = 0;
    this.state = 'idle';
    this.scanForm.reset();
  }

  private loadComponent(id: number): void {
    this.isLoadingComponent = true;
    this.documentScanService
      .getComponent(id)
      .pipe(finalize(() => (this.isLoadingComponent = false)))
      .subscribe({
        next: (response) => {
          this.scanComponent = {
            ...response,
            view: response.view || this.initConfig?.view,
            permissions: response.permissions || this.initConfig?.permissions,
            spaceAfter: this.initConfig?.spaceAfter
          };
          this.events.pages$$.next(this.scanComponent.title);
          this.setPermissions();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 403) {
            this.forbidden = true;
            this.canView = false;
            return;
          }
          this.state = 'error';
          const mappedError = this.mapHttpError(error);
          this.errorMessage = mappedError.message;
          this.isRecoverableError = mappedError.recoverable;
        }
      });
  }

  private setPermissions(): void {
    const permissions = this.scanComponent?.permissions || this.initConfig?.permissions;
    if (!permissions) {
      this.canView = true;
      this.canEdit = true;
      return;
    }
    this.canView = !!permissions.view || !!permissions.edit || !!permissions.super;
    // En document scan, si puede visualizar el componente también puede completar el flujo.
    this.canEdit = this.canView;
    this.forbidden = !this.canView;
  }

  private static readonly MAX_FILE_BYTES = 30 * 1024 * 1024;

  /**
   * Garantiza un File/Blob limpio para subir. En iOS/Safari los objetos con
   * name: [Blob] pueden corromperse al enviar; creamos un File explícito.
   */
  private ensureUploadableFile(
    fileOrBlob: File | Blob | any,
    fileName: string,
    mimeType: string
  ): File {
    let blob: Blob;
    if (fileOrBlob instanceof Blob || fileOrBlob instanceof File) {
      blob = fileOrBlob;
    } else if (fileOrBlob?.blob instanceof Blob) {
      blob = fileOrBlob.blob;
    } else if (Array.isArray(fileOrBlob?.name) && fileOrBlob.name[0] instanceof Blob) {
      blob = fileOrBlob.name[0];
    } else {
      blob = fileOrBlob as Blob;
    }
    const ext = mimeType.includes('png') ? 'png' : mimeType.includes('webp') ? 'webp' : 'jpg';
    const name = /\.(jpg|jpeg|png|webp)$/i.test(fileName) ? fileName : `${fileName.replace(/\.[^.]+$/, '') || 'document'}.${ext}`;
    return new File([blob], name, { type: mimeType || 'image/jpeg' });
  }

  /**
   * Obtiene el tamaño real del archivo. En iOS/Safari algunos objetos file-like
   * tienen size: 0 pero el Blob interno (p. ej. en name[0]) sí tiene datos.
   */
  private getActualFileSize(file: File | Blob | any): number {
    if (file instanceof Blob || file instanceof File) {
      const s = file.size;
      if (typeof s === 'number' && s > 0) return s;
    }
    if (file && typeof file.size === 'number' && file.size > 0) return file.size;
    if (file?.blob instanceof Blob && file.blob.size > 0) return file.blob.size;
    if (Array.isArray(file?.name) && file.name[0] instanceof Blob) {
      const blobSize = file.name[0].size;
      if (typeof blobSize === 'number' && blobSize > 0) return blobSize;
    }
    return 0;
  }

  private getFileType(file: File | Blob): string {
    const t = file?.type;
    if (typeof t === 'string' && t) return t;
    const obj = t != null && typeof t === 'object' ? (t as { type?: string }) : null;
    if (obj && typeof obj.type === 'string') return obj.type;
    if (file && 'name' in file) {
      const name = String((file as File).name || '').toLowerCase();
      if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
      if (name.endsWith('.png')) return 'image/png';
      if (name.endsWith('.webp')) return 'image/webp';
    }
    return '';
  }

  private async uploadSelectedFile(
    file: File,
    meta?: { knownSize?: number; knownMimeType?: string; previewDataUrl?: string; base64Data?: string }
  ): Promise<void> {
    if (!this.scanComponent?.id) return;
    if (this.state === 'uploading' || this.state === 'processing') return;
    const normalizedFile = this.normalizeToFile(file);
    if (!normalizedFile) {
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-FILE-FORMAT-INVALID';
      this.isRecoverableError = true;
      return;
    }
    let fileToUpload = normalizedFile;
    const fileType = meta?.knownMimeType || this.getFileType(normalizedFile);
    let fileSize =
      meta && typeof meta.knownSize === 'number' ? meta.knownSize : (typeof normalizedFile.size === 'number' ? normalizedFile.size : 0);
    if (fileSize === 0) fileSize = this.getActualFileSize(file) || this.getActualFileSize(normalizedFile);
    const needsCompress = fileType.startsWith('image/') && fileSize > DocumentScanComponent.MAX_FILE_BYTES;
    let uploadBlob: Blob = normalizedFile;
    let uploadFileName = typeof normalizedFile.name === 'string' && normalizedFile.name ? normalizedFile.name : `document-scan-${Date.now()}.jpg`;
    if (needsCompress) {
      const compressed = await this.compressImageToMaxSize(normalizedFile, DocumentScanComponent.MAX_FILE_BYTES);
      if (compressed) {
        fileToUpload = compressed.file;
        uploadBlob = compressed.blob;
        uploadFileName = typeof compressed.file.name === 'string' && compressed.file.name ? compressed.file.name : uploadFileName;
      }
    }
    const validationMeta = {
      size: fileSize,
      mimeType: fileType
    };
    const validationError = this.validateFile(fileToUpload, validationMeta);
    if (validationError) {
      this.state = 'error';
      this.errorMessage = validationError;
      this.isRecoverableError = true;
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    const isImage = this.getFileType(fileToUpload).startsWith('image/');
    if (isImage) {
      try {
        this.previewUrl = URL.createObjectURL(fileToUpload);
      } catch {
        this.previewUrl = meta?.previewDataUrl ?? '';
      }
    } else {
      this.previewUrl = '';
    }
    this.state = 'uploading';
    if (fileToUpload === normalizedFile && meta?.previewDataUrl && meta?.knownMimeType) {
      uploadBlob = this.fileService.base64ToBlob(meta.previewDataUrl, meta.knownMimeType);
      uploadFileName = typeof fileToUpload.name === 'string' && fileToUpload.name ? fileToUpload.name : uploadFileName;
    }
    let blobToSend: File | Blob;
    try {
      if (meta?.base64Data && meta?.knownMimeType) {
        const blob = this.fileService.base64ToBlob(
          `data:${meta.knownMimeType};base64,${meta.base64Data}`,
          meta.knownMimeType
        );
        const ext = meta.knownMimeType.includes('png') ? 'png'
          : meta.knownMimeType.includes('webp') ? 'webp'
          : 'jpg';
        uploadFileName = /\.(jpg|jpeg|png|webp)$/i.test(uploadFileName) ? uploadFileName : `document.${ext}`;
        blobToSend = new File([blob], uploadFileName, { type: meta.knownMimeType });
      } else {
        blobToSend = this.ensureUploadableFile(uploadBlob, uploadFileName, fileType);
      }
    } catch (err) {
      console.error('[document-scan] ensureUploadableFile error:', err);
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-GENERIC-ERROR';
      this.isRecoverableError = true;
      return;
    }
    this.documentScanService.uploadImage(this.scanComponent.id, blobToSend, uploadFileName).subscribe({
      next: (response) => {
        this.responseId = response.id;
        this.state = 'processing';
        this.startPolling(response.id);
      },
      error: (error: HttpErrorResponse) => {
        this.state = 'error';
        console.error('[document-scan] upload error:', error?.error || error?.message || error);
        const mappedError = this.mapHttpError(error);
        this.errorMessage = mappedError.message;
        this.isRecoverableError = mappedError.recoverable;
      }
    });
  }

  private startPolling(responseId: number): void {
    this.stopPolling();
    this.processingStartTime = Date.now();
    this.state = 'processing';
    timer(0, 2500)
      .pipe(
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        switchMap(() => this.documentScanService.getResponse(responseId))
      )
      .subscribe({
        next: (response) => this.handlePollingResponse(response),
        error: (error: HttpErrorResponse) => {
          this.stopPolling();
          this.state = 'error';
          const mappedError = this.mapHttpError(error);
          this.errorMessage = mappedError.message;
          this.isRecoverableError = mappedError.recoverable;
        }
      });
  }

  private stopPolling(): void {
    this.stopPolling$.next();
    this.isSlowProcessing = false;
    this.processingSeconds = 0;
  }

  private handlePollingResponse(response: ScanResponse): void {
    this.responseData = response;
    if (response.imageUrl || response.imageFile?.url) {
      this.previewUrl = response.imageUrl || response.imageFile.url;
    }
    this.processingSeconds = Math.floor((Date.now() - this.processingStartTime) / 1000);
    this.isSlowProcessing = this.processingSeconds >= 60;

    if (response.status === 'processing') {
      this.state = 'processing';
      return;
    }
    if (response.status === 'pending_user') {
      this.stopPolling();
      this.initDynamicForm(response);
      this.state = 'pending_user';
      this.cdr.detectChanges();
      return;
    }
    if (response.status === 'pending_review') {
      this.stopPolling();
      this.state = 'pending_review';
      this.successMessage = 'DOCUMENT-SCAN-SUBMIT-SUCCESS';
      return;
    }
    if (response.status === 'error') {
      this.stopPolling();
      this.state = 'error';
      this.errorMessage = response.errorMessage || 'DOCUMENT-SCAN-PROCESSING-ERROR';
      this.isRecoverableError = true;
    }
  }

  private initDynamicForm(response: ScanResponse): void {
    const fields = [...(response.fields || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    this.orderedFields = fields;
    const legacyData = (response.currentData || response.userEditedData || response.llmResponse || {}) as Record<string, any>;
    this.initialEditableData = {};
    const formGroup: Record<string, UntypedFormControl> = {};

    fields.forEach((field) => {
      const controlName = String(field.fieldId);
      const rawValue = field.currentValue !== undefined
        ? field.currentValue
        : legacyData[String(field.fieldId)];
      const value = this.getInitialFieldValue(field, rawValue);
      this.initialEditableData[controlName] = value;
      formGroup[controlName] = new UntypedFormControl(value, this.buildFieldValidators(field));
    });

    this.scanForm = new UntypedFormGroup(formGroup);
  }

  private getInitialFieldValue(field: ScanField, value: any): any {
    if (value === null || value === undefined) {
      if (field.type === 'multiple_option' || field.type === 'table') return [];
      return field.type === 'number' ? null : '';
    }
    return value;
  }

  private buildFieldValidators(field: ScanField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field.required) validators.push(Validators.required);
    if (field.type === 'number') validators.push(this.numberFieldValidator());
    if (field.type === 'date') validators.push(this.dateFieldValidator());
    if (field.type === 'single_option') validators.push(this.singleOptionValidator(field));
    if (field.type === 'multiple_option') validators.push(this.multipleOptionValidator(field));
    if (field.type === 'table') validators.push(this.tableValidator(field));
    return validators;
  }

  private numberFieldValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '' || control.value === undefined) return null;
      return Number.isNaN(Number(control.value)) ? { invalidNumber: true } : null;
    };
  }

  private dateFieldValidator(): ValidatorFn {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      if (!dateRegex.test(control.value)) return { invalidDate: true };
      const date = new Date(control.value);
      return Number.isNaN(date.getTime()) ? { invalidDate: true } : null;
    };
  }

  private singleOptionValidator(field: ScanField): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const options = field.metadata?.options || [];
      return options.includes(control.value) ? null : { invalidOption: true };
    };
  }

  private multipleOptionValidator(field: ScanField): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !Array.isArray(control.value)) return null;
      const options = field.metadata?.options || [];
      const invalid = control.value.some((selected) => !options.includes(selected));
      return invalid ? { invalidOptions: true } : null;
    };
  }

  private tableValidator(field: ScanField): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return field.required ? { required: true } : null;
      if (!Array.isArray(control.value)) return { invalidTable: true };
      if (field.required && control.value.length === 0) return { required: true };

      const columns = field.metadata?.columns || [];
      for (const row of control.value) {
        if (typeof row !== 'object' || row === null) return { invalidTable: true };
        for (const column of columns) {
          const key = this.getColumnKey(column);
          const cellValue = row[key];
          if (cellValue === null || cellValue === '' || cellValue === undefined) continue;
          if (column.type === 'number' && Number.isNaN(Number(cellValue))) return { invalidTableNumber: true };
          if (column.type === 'date' && this.dateFieldValidator()({ value: cellValue } as AbstractControl)) return { invalidTableDate: true };
        }
      }
      return null;
    };
  }

  private buildSubmitPayload(): Record<number, any> {
    const payload: Record<number, any> = {};
    this.orderedFields.forEach((field) => {
      const controlName = String(field.fieldId);
      const value = this.scanForm.get(controlName)?.value;
      payload[field.fieldId] = this.normalizeSubmitValue(field, value);
    });
    return payload;
  }

  private normalizeSubmitValue(field: ScanField, value: any): any {
    if (field.type === 'number') {
      if (value === null || value === '') return null;
      return Number(value);
    }
    if (field.type === 'multiple_option') return Array.isArray(value) ? value : [];
    if (field.type === 'table') return this.normalizeTableSubmit(field, value);
    if (field.type === 'date' || field.type === 'single_line' || field.type === 'multiple_line' || field.type === 'single_option') {
      return value ?? '';
    }
    return value;
  }

  private normalizeTableSubmit(field: ScanField, value: any): any[] {
    const rows = Array.isArray(value) ? value : [];
    const columns = field.metadata?.columns || [];
    return rows.map((row) => {
      const normalizedRow: any = {};
      columns.forEach((column) => {
        const key = this.getColumnKey(column);
        normalizedRow[key] = this.normalizeTableCellValue(column.type, row?.[key]);
      });
      return normalizedRow;
    });
  }

  private normalizeTableCellValue(type: string, value: any): any {
    if (value === null || value === undefined || value === '') return '';
    if (type === 'number') return Number(value);
    return value;
  }

  private markFormTouched(): void {
    Object.keys(this.scanForm.controls).forEach((controlName) => {
      this.scanForm.controls[controlName].markAsTouched();
      this.scanForm.controls[controlName].updateValueAndValidity();
    });
  }

  private validateFile(file: File, meta?: { size?: number; mimeType?: string }): string {
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const mime = meta?.mimeType || this.getFileType(file);
    const size =
      meta && typeof meta.size === 'number' ? meta.size : (typeof file.size === 'number' ? file.size : 0);
    if (size === 0) return 'DOCUMENT-SCAN-FILE-EMPTY';
    const sizeOk = size <= DocumentScanComponent.MAX_FILE_BYTES;
    const mimeOk = !!mime && allowedMimeTypes.includes(mime);
    if (!mimeOk) return 'DOCUMENT-SCAN-FILE-FORMAT-INVALID';
    if (!sizeOk) return 'DOCUMENT-SCAN-FILE-SIZE-INVALID';
    return '';
  }

  /**
   * Comprime una imagen para que no supere maxBytes. Redimensiona y exporta como JPEG.
   * Retorna null si falla. Solo aplicable a imágenes (jpeg, png, webp).
   * Retorna { file, blob } para poder subir el blob en entornos donde File no es instancia de Blob.
   */
  private async compressImageToMaxSize(file: File, maxBytes: number): Promise<{ file: File; blob: Blob } | null> {
    const maxDimension = 2048;

    return new Promise<{ file: File; blob: Blob } | null>((resolve) => {
      const img = new Image();
      let url: string;
      try {
        url = URL.createObjectURL(file);
      } catch {
        resolve(null);
        return;
      }
      img.onload = () => {
        URL.revokeObjectURL(url);
        let width = img.width;
        let height = img.height;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const tryQuality = (q: number): void => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(null);
                return;
              }
              if (blob.size <= maxBytes || q <= 0.3) {
                const name = typeof file.name === 'string' ? file.name.replace(/\.[^.]+$/, '.jpg') : 'image.jpg';
                resolve({ file: new File([blob], name, { type: 'image/jpeg' }), blob });
                return;
              }
              tryQuality(Math.max(0.3, q - 0.15));
            },
            'image/jpeg',
            q
          );
        };
        tryQuality(0.85);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  private mapHttpError(error: HttpErrorResponse): { message: string; recoverable: boolean } {
    // Primero revisar si el backend envía un código específico en el cuerpo
    const errorBody = error.error;
    if (errorBody && typeof errorBody === 'object') {
      if (errorBody.code === 'daily_limit_reached') {
        return { message: 'DOCUMENT-SCAN-DAILY-LIMIT', recoverable: false };
      }
      if (errorBody.code === 'invalid_file_format') {
        return { message: 'DOCUMENT-SCAN-FILE-FORMAT-INVALID', recoverable: true };
      }
      if (errorBody.code === 'file_too_large' || errorBody.code === 'invalid_file_size') {
        return { message: 'DOCUMENT-SCAN-FILE-SIZE-INVALID', recoverable: true };
      }
      // Si el backend manda un mensaje de error descriptivo, mostrarlo directamente
      if (errorBody.error && typeof errorBody.error === 'string') {
        return { message: errorBody.error, recoverable: true };
      }
      if (errorBody.message && typeof errorBody.message === 'string') {
        return { message: errorBody.message, recoverable: true };
      }
    }

    // Fallback por status HTTP
    if (error.status === 400) return { message: 'DOCUMENT-SCAN-BAD-REQUEST', recoverable: true };
    if (error.status === 403) return { message: 'DOCUMENT-SCAN-DAILY-LIMIT', recoverable: false };
    if (error.status === 404) return { message: 'DOCUMENT-SCAN-NOT-AVAILABLE', recoverable: false };
    if (error.status === 500) return { message: 'DOCUMENT-SCAN-SERVER-ERROR', recoverable: true };
    return { message: 'DOCUMENT-SCAN-GENERIC-ERROR', recoverable: true };
  }

  /**
   * Convierte una foto de la cámara/galería en File.
   * Mismo flujo que galería: getImageBase64 (web) o convertFilePathToBase64(path || webPath) (native).
   * En native se espera un poco antes de leer, como en galería (el ActionSheet cierra antes de usar la foto).
   */
  /** Resultado con File y metadatos conocidos (para entornos donde File tiene type/size incorrectos). */
  private async photoToFile(
    photo: { webPath?: string; path?: string; format?: string },
    extension: string
  ): Promise<{ file: File; knownSize: number; knownMimeType: string; previewDataUrl: string; base64Data?: string } | null> {
    try {
      let base64String: string;

      if (this.platformService.isWeb()) {
        const base64Image = await this.fileService.getImageBase64(photo.webPath!);
        base64String = base64Image.includes(',') ? base64Image.split(',')[1] : base64Image;
      } else {
        await this.delay(400);
        try {
          base64String = await this.readPhotoWithFilesystem(photo);
        } catch {
          const raw = await this.fileService.convertFilePathToBase64(photo.path || photo.webPath!);
          base64String = typeof raw === 'string' ? raw : String(raw);
        }
        base64String = base64String.replace(/\s/g, '');
      }

      const base64Image =
        base64String.includes(',') ? base64String : `data:image/${extension};base64,${base64String}`;
      const blob = this.fileService.base64ToBlob(base64Image, `image/${extension}`);
      const fileName = `document-scan-${Date.now()}.${extension === 'jpeg' ? 'jpg' : extension}`;
      const mimeType = `image/${extension}`;
      const file = new File([blob], fileName, { type: mimeType });
      const previewDataUrl = base64Image.startsWith('data:') ? base64Image : `data:image/${extension};base64,${base64String}`;
      const rawBase64 = base64String.includes(',') ? base64String.split(',')[1] : base64String;
      return { file, knownSize: blob.size, knownMimeType: mimeType, previewDataUrl, base64Data: rawBase64 };
    } catch (error) {
      console.error('document-scan photoToFile error', error);
      this.state = 'error';
      this.errorMessage = 'DOCUMENT-SCAN-GENERIC-ERROR';
      this.isRecoverableError = true;
      return null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Intenta leer la foto con Filesystem (solo en native). En iOS convertFilePathToBase64
   * usa fetch(webPath) para /tmp/ y falla; aquí probamos rutas que el plugin iOS pueda aceptar.
   */
  private async readPhotoWithFilesystem(
    photo: { path?: string; webPath?: string }
  ): Promise<string> {
    const fullPath = photo.path?.replace(/^file:\/\//, '') || '';
    const filename = fullPath.replace(/^.*[/\\]/, '') || '';
    const pathVariants = [
      photo.path,
      fullPath,
      fullPath.replace(/^\/+/, ''),
      filename,
      filename ? `tmp/${filename}` : ''
    ].filter(Boolean) as string[];

    for (const p of pathVariants) {
      try {
        const contents = await Filesystem.readFile({ path: p });
        const data = contents.data;
        if (typeof data === 'string') return data;
        if (data instanceof ArrayBuffer) return this.arrayBufferToBase64(data);
        if (ArrayBuffer.isView(data)) {
          const buf = (data as { buffer: ArrayBuffer }).buffer;
          return this.arrayBufferToBase64(buf);
        }
      } catch {
        continue;
      }
    }
    if (filename) {
      for (const dir of [Directory.Cache, Directory.Data]) {
        try {
          const contents = await Filesystem.readFile({ path: filename, directory: dir });
          const data = contents.data;
          if (typeof data === 'string') return data;
          if (data instanceof ArrayBuffer) return this.arrayBufferToBase64(data);
          if (ArrayBuffer.isView(data)) {
            const buf = (data as { buffer: ArrayBuffer }).buffer;
            return this.arrayBufferToBase64(buf);
          }
        } catch {
          continue;
        }
      }
    }
    throw new Error('Filesystem read failed');
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
